import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
// shared
import ModuleTitleBar from "Components/shared/module-title-bar";
import UserCreateForm from "Components/user-management/user-create-form/user-create-form";
// helpers
import { decode, toaster } from "Helpers/index";
// hooks
import { useAccessControlList, useSession } from "Hooks/index";
// types
import {
  FetchedUserData,
  UserCreationInput,
  UserProfileCategoriesTab,
} from "Types/user.types";
import { fetchSingleUser, updateUser } from "Services/user.services";

const UserUpdate = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, orgID } = useParams<{ id: string; orgID: string }>();
  const { org, roleTypes, profile } = useSession();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "User_Management",
  });
  const userID = decode(id) || "";
  const decodedOrgID = decode(orgID) || "";
  const canUpdate = permission.rights.includes("UPDATE");

  const [userDetails, setUserDetails] = useState<FetchedUserData>();

  useEffect(() => {
    if (userID) {
      handleFetchUserDetails();
    }
  }, [userID]);

  const handleFetchUserDetails = async () => {
    const responseData = await fetchSingleUser(
      profile._id,
      org._id,
      userID,
      decodedOrgID,
    );

    if (responseData && !responseData.error) {
      setUserDetails(responseData);
    } else {
      toaster("error", responseData?.error?.message);
    }
  };

  const handleUpdateUser = async (data: UserCreationInput) => {
    setIsLoading(true);

    const responseData = await updateUser(data);

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.userInfoUpdated"));
      setIsLoading(false);
      history.goBack();
    } else {
      setIsLoading(false);

      if (typeof responseData.error.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  return canUpdate ? (
    <div>
      <Helmet>
        <title>{t("pageTitle.editUserProfile")}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.userManagement")}
        haveSubtitle
        subtitleText={t("title.user")}
      />

      {
        <UserCreateForm
          onBack={() => {
            history.goBack();
          }}
          onSubmitEditForm={handleUpdateUser}
          userDetails={userDetails}
          isLoading={isLoading}
        />
      }
    </div>
  ) : (
    <></>
  );
};

export default UserUpdate;
